import { useEffect, useState } from 'react';
import { useI18n } from '../../i18n/useI18n';
import { resolveBackendBaseUrl } from '../../utils/backendBaseUrl';
import type { ChatbotWithIdentity } from '../../types/chatbot';

const EMBED_SCRIPT_ID = 'liks-chat-embed-script';

type LiksChatEmbedApi = {
  mountChatWidget: (options: {
    chatbotId: string;
    apiBase: string;
  }) => Promise<void>;
  unmountChatWidget: (chatbotId?: string) => void;
};

function getEmbedApi(): LiksChatEmbedApi | null {
  const api = window.LiksChatEmbed;
  if (
    api &&
    typeof api.mountChatWidget === 'function' &&
    typeof api.unmountChatWidget === 'function'
  ) {
    return api;
  }
  return null;
}

function loadEmbedScript(): Promise<LiksChatEmbedApi> {
  const existing = getEmbedApi();
  if (existing) {
    return Promise.resolve(existing);
  }

  return new Promise((resolve, reject) => {
    const existingScript = document.getElementById(EMBED_SCRIPT_ID);
    if (existingScript) {
      existingScript.addEventListener(
        'load',
        () => {
          const api = getEmbedApi();
          if (api) {
            resolve(api);
            return;
          }
          reject(new Error('LiksChatEmbed API missing after script load'));
        },
        { once: true },
      );
      existingScript.addEventListener(
        'error',
        () => reject(new Error('Failed to load embed.js')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.id = EMBED_SCRIPT_ID;
    script.async = true;
    script.src = `/embed.js?v=${Date.now()}`;
    script.addEventListener(
      'load',
      () => {
        const api = getEmbedApi();
        if (api) {
          resolve(api);
          return;
        }
        reject(new Error('LiksChatEmbed API missing after script load'));
      },
      { once: true },
    );
    script.addEventListener(
      'error',
      () => reject(new Error('Failed to load embed.js')),
      { once: true },
    );
    document.body.appendChild(script);
  });
}

declare global {
  interface Window {
    LiksChatEmbed?: LiksChatEmbedApi;
  }
}

type ChatbotEmbedPreviewProps = {
  chatbot: ChatbotWithIdentity;
  /** When false, only mounts the widget without inline status text. */
  showStatus?: boolean;
};

export function ChatbotEmbedPreview({
  chatbot,
  showStatus = true,
}: ChatbotEmbedPreviewProps) {
  const { translate } = useI18n();
  const [mountError, setMountError] = useState<string | null>(null);
  const [mountedChatbotId, setMountedChatbotId] = useState<string | null>(null);

  const chatbotId = chatbot.id;
  const isPublic = chatbot.isPublic === true;
  const apiBase = resolveBackendBaseUrl();

  // Remount when saved config that the public widget reads changes.
  const previewKey = [
    chatbot.id,
    chatbot.displayName ?? '',
    chatbot.primaryColor ?? '',
    chatbot.welcomeMessage ?? '',
    chatbot.placement ?? '',
    isPublic ? 'public' : 'private',
    chatbot.stamp ?? '',
  ].join('|');

  useEffect(() => {
    if (!isPublic) {
      getEmbedApi()?.unmountChatWidget(chatbotId);
      return;
    }

    let cancelled = false;

    const mountPreview = async () => {
      try {
        const api = await loadEmbedScript();
        if (cancelled) {
          return;
        }

        api.unmountChatWidget(chatbotId);
        await api.mountChatWidget({
          chatbotId,
          apiBase,
        });

        if (!cancelled) {
          setMountedChatbotId(chatbotId);
          setMountError(null);
        }
      } catch (error) {
        if (cancelled) {
          return;
        }
        setMountedChatbotId(null);
        setMountError(
          error instanceof Error
            ? error.message
            : translate('chatbots.settings.preview.loadError'),
        );
      }
    };

    void mountPreview();

    return () => {
      cancelled = true;
      getEmbedApi()?.unmountChatWidget(chatbotId);
    };
  }, [apiBase, chatbotId, isPublic, previewKey, translate]);

  if (!showStatus) {
    return null;
  }

  if (!isPublic) {
    return (
      <p className="chatbot-settings__preview-status">
        {translate('chatbots.settings.preview.notPublic')}
      </p>
    );
  }

  if (mountError != null) {
    return (
      <p className="chatbot-settings__preview-status" role="alert">
        {translate('chatbots.settings.preview.loadError')}
        {mountError ? ` ${mountError}` : ''}
      </p>
    );
  }

  if (mountedChatbotId !== chatbotId) {
    return null;
  }

  return (
    <p className="chatbot-settings__preview-status chatbot-settings__preview-status--ready">
      {translate('chatbots.settings.preview.active', {
        origin: window.location.origin,
      })}
    </p>
  );
}
